// AnnouncementComposer.jsx
// Create/post form for admins and staff. Used by AnnouncementsCaster (admin
// dashboard widget) and the standalone AnnouncementsPage.
import React, { useRef, useState } from "react";
import {
  Send, Calendar, Paperclip, X, Pin, MessageSquare, Smile, Loader2,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON  = "#00FF99";
const AMBER = "#EF9F27";

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "policy",  label: "Policy" },
  { value: "event",   label: "Event" },
  { value: "kudos",   label: "Kudos" },
  { value: "urgent",  label: "Urgent" },
];

const TARGETS = [
  { value: "company",     label: "Everyone" },
  { value: "department",  label: "Department" },
  { value: "role",        label: "Role" },
];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const ACCEPT = ".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv";

export default function AnnouncementComposer({
  endpoint = "/api/admin/announcements",
  onPosted,
  compact = false,
}) {
  const [title,       setTitle]       = useState("");
  const [body,        setBody]        = useState("");
  const [category,    setCategory]    = useState("general");
  const [target,      setTarget]      = useState("company");
  const [allowComments,  setAllowComments]  = useState(true);
  const [allowReactions, setAllowReactions] = useState(true);
  const [pinned,      setPinned]      = useState(false);
  const [schedule,    setSchedule]    = useState("");

  const [file,        setFile]        = useState(null);   // {url, name, type}
  const [uploading,   setUploading]   = useState(false);
  const [uploadErr,   setUploadErr]   = useState(null);
  const [dragOver,    setDragOver]    = useState(false);

  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState(null);
  const [done,        setDone]        = useState(false);

  const fileRef = useRef(null);

  const uploadFile = async (f) => {
    setUploading(true);
    setUploadErr(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API_BASE}/api/announcements/upload-attachment`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Upload failed (${res.status}): ${txt}`);
      }
      const d = await res.json();
      setFile({ url: d.url, name: d.name, type: d.type });
    } catch (e) {
      setUploadErr(e.message);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadFile(f);
  };

  const submit = async () => {
    if (!title.trim()) { setError("Title is required"); return; }
    setSubmitting(true);
    setError(null);
    setDone(false);
    try {
      const payload = {
        title:           title.trim(),
        body:            body.trim() || null,
        category,
        target_audience: target,
        visibility:      target === "company" ? "company" : target,
        allow_comments:  allowComments,
        allow_reactions: allowReactions,
        pinned,
        scheduled_at:    schedule ? new Date(schedule).toISOString() : null,
        attachment_url:  file?.url || null,
        attachment_name: file?.name || null,
        attachment_type: file?.type || null,
      };
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      const d = await res.json();
      setDone(true);
      setTitle(""); setBody(""); setSchedule("");
      setFile(null); setPinned(false);
      if (onPosted) onPosted(d);
      setTimeout(() => setDone(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inp = {
    background: "#0a0a0a",
    border: "0.5px solid #2a2a2a",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    color: "#fff",
    outline: "none",
    width: "100%",
  };

  const label = { fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4, display: "block" };

  const toggle = (on, setOn, text, Icon) => (
    <button
      type="button"
      onClick={() => setOn(!on)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 10px", borderRadius: 8, fontSize: 11,
        border: on ? `0.5px solid ${NEON}77` : "0.5px solid #2a2a2a",
        background: on ? "rgba(0,255,153,0.08)" : "transparent",
        color: on ? NEON : "#888",
        cursor: "pointer",
      }}
    >
      <Icon size={11} /> {text}
    </button>
  );

  return (
    <div style={{
      background: "#0d0d0d",
      border: "0.5px solid #1c1c1c",
      borderRadius: 12,
      padding: compact ? 12 : 16,
    }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={label}>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Announcement title"
            style={inp}
          />
        </div>
        <div>
          <label style={label}>Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={inp}>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <label style={label}>Body</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={compact ? 3 : 4}
          placeholder="Write something for the team…"
          style={{ ...inp, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
        <div>
          <label style={label}>Target audience</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)} style={inp}>
            {TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>Schedule (optional)</label>
          <input
            type="datetime-local"
            value={schedule}
            onChange={(e) => setSchedule(e.target.value)}
            style={inp}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        {toggle(allowComments,  setAllowComments,  "Comments",  MessageSquare)}
        {toggle(allowReactions, setAllowReactions, "Reactions", Smile)}
        {toggle(pinned,         setPinned,         "Pin",       Pin)}
      </div>

      {/* Attachment */}
      <div style={{ marginTop: 12 }}>
        <label style={label}>Attachment (optional)</label>
        {file ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: 10, borderRadius: 8,
            background: "rgba(0,255,153,0.05)",
            border: `0.5px solid ${NEON}33`,
          }}>
            <Paperclip size={14} color={NEON} />
            <span style={{ fontSize: 12, color: "#ddd", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
              {file.name}
            </span>
            <button
              type="button"
              onClick={() => setFile(null)}
              style={{ background: "transparent", border: "none", color: "#888", cursor: "pointer" }}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileRef.current?.click()}
            style={{
              padding: 16,
              borderRadius: 8,
              border: `0.5px dashed ${dragOver ? NEON : "#2a2a2a"}`,
              background: dragOver ? "rgba(0,255,153,0.04)" : "rgba(255,255,255,0.02)",
              textAlign: "center",
              cursor: "pointer",
              fontSize: 12,
              color: "#888",
            }}
          >
            {uploading ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Loader2 size={12} className="animate-spin" /> Uploading…
              </span>
            ) : (
              <>
                <Paperclip size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
                Drag a file here, or click to choose
                <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>
                  Images, PDF, DOC, XLSX, PPT — max 15 MB
                </div>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              style={{ display: "none" }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); }}
            />
          </div>
        )}
        {uploadErr && <p style={{ color: "#e66", fontSize: 11, marginTop: 4 }}>⚠ {uploadErr}</p>}
      </div>

      {/* Submit */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
        <button
          onClick={submit}
          disabled={submitting || !title.trim()}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "9px 16px", borderRadius: 8, fontSize: 12, fontWeight: 500,
            background: "rgba(0,255,153,0.15)",
            border: `0.5px solid ${NEON}55`,
            color: NEON,
            cursor: submitting ? "wait" : "pointer",
            opacity: !title.trim() ? 0.4 : 1,
          }}
        >
          {schedule ? <Calendar size={13} /> : <Send size={13} />}
          {submitting ? "Posting…" : schedule ? "Schedule" : "Post now"}
        </button>

        {error && <span style={{ fontSize: 11, color: "#e66" }}>⚠ {error}</span>}
        {done  && <span style={{ fontSize: 11, color: NEON }}>✓ Posted</span>}
      </div>
    </div>
  );
}
